import { PropertyHelper } from '@/helpers/property.helper';
import { AlertService } from '@/services/alert.service';
import { DictionaryCacheService } from '@/services/dictionary-cache.service';
import { AttributeValue } from '@/views/property/condo-management/condo-management.component';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import {
  Attribute,
  AttributeGroup,
  AttributeType,
  PropertiesListAttributes,
  PropertiesManagementService,
  PropertyAttribute,
  PropertyType,
  RealProperty
} from '@eemo/api-client';
import { InputTrimDirective } from '../../extensions/inputTrimDirective';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';

import { MatButtonModule } from '@angular/material/button';
import { NgIf, NgFor } from '@angular/common';

export interface PropertyAttributesAddDialogData {
  props: RealProperty[];
}

@Component({
    selector: 'app-property-attributes-add-dialog',
    templateUrl: './property-attributes-add-dialog.component.html',
    styleUrls: ['./property-attributes-add-dialog.component.scss'],
    standalone: true,
    imports: [MatDialogModule, NgIf, FormsModule, ReactiveFormsModule, MatButtonModule, MatMenuModule, MatIconModule, NgFor, MatFormFieldModule, MatInputModule, InputTrimDirective]
})
export class PropertyAttributesAddDialogComponent implements OnInit {
  dialogTitle: string;
  formSubmitting = false;
  attrsForm: UntypedFormGroup;

  availablePropTypes: PropertyType[];
  filterAttributesSelected: { [key: string]: AttributeValue } = {};

  filterDisplayAttributeGroups: AttributeGroup[];
  filterDisplayAttributes: { [key: string]: Attribute[] } = {};
  filterDisplayAttributeValues: { [key: string]: AttributeValue[] } = {};

  constructor(
    public helper: PropertyHelper,
    public dialogRef: MatDialogRef<PropertyAttributesAddDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: PropertyAttributesAddDialogData,
    private fb: UntypedFormBuilder,
    public propMngtService: PropertiesManagementService,
    private dictCacheService: DictionaryCacheService,
    private alertService: AlertService
  ) {}

  // convenience getter for easy access to form fields
  f(field: string): AbstractControl {
    return this.attrsForm.get(field);
  }

  ngOnInit() {
    this.dialogTitle = 'Modifier les attributs';

    this.attrsForm = this.fb.group({
      attrs: this.fb.array([])
    });

    this.loadAttributeList();
  }

  loadAttributeList() {
    this.availablePropTypes = [];
    this.data.props.forEach((element) => {
      if (!this.availablePropTypes.includes(element.type)) {
        this.availablePropTypes.push(element.type);
      }
    });
    // build the attributes filter based on the available types
    this.filterDisplayAttributes = {};
    this.filterDisplayAttributeValues = {};
    this.filterDisplayAttributeGroups = this.dictCacheService.getAttributeGroupsForCategory('BUILDING');
    this.filterDisplayAttributeGroups.forEach((group) => {
      const attrs = this.dictCacheService.getAttributesForGroupAndPropertyTypes(group.code, this.availablePropTypes);
      if (attrs.length > 0) {
        this.filterDisplayAttributes[group.code] = attrs;
      } else {
        this.filterDisplayAttributeGroups.splice(this.filterDisplayAttributeGroups.indexOf(group), 1);
      }
      attrs.forEach((attr) => {
        if (attr.type === AttributeType.BOOLEAN) {
          this.filterDisplayAttributeValues[attr.code] = [
            { name: 'Oui', value: 'true' },
            { name: 'Non', value: 'false' }
          ];
        } else if (attr.type === AttributeType.ENUM && attr.availableValues) {
          this.filterDisplayAttributeValues[attr.code] = [];
          attr.availableValues.forEach((value) =>
            this.filterDisplayAttributeValues[attr.code].push({ name: value.display.name, value: value.code })
          );
        }
      });
    });
  }

  get attrs(): UntypedFormArray {
    return this.f('attrs') as UntypedFormArray;
  }

  addFilterAttributeValue(attr: Attribute, value: any, displayValue: string) {
    this.attrs.push(
      this.fb.group({
        code: [attr.code, Validators.required],
        displayName: [attr.display.name],
        value: [value, Validators.required],
        displayValue: [displayValue]
      })
    );
  }

  deleteAttr(index) {
    this.attrs.removeAt(index);
  }

  onSubmit() {
    if (this.attrsForm.valid && this.attrs.length > 0) {
      this.formSubmitting = true;

      // prepare attributes to update
      const attrsToSubmit: PropertiesListAttributes = {
        propertiesId: this.data.props.map((p) => p.uid),
        attributes: []
      };
      this.attrs.controls.forEach((c) => {
        const propAttr: PropertyAttribute = { code: c.get('code').value, value: c.get('value').value };
        attrsToSubmit.attributes.push(propAttr);
      });
      this.propMngtService.updateRealPropertiesAttributes(attrsToSubmit).subscribe(
        (result) => {
          this.alertService.success('Modification effectuée avec succès');
          this.dialogRef.close(true);
        },
        (err: HttpErrorResponse) => {
          this.dialogRef.close(true);
        }
      );
    }
  }

  closeDialog() {
    return this.dialogRef.close(false);
  }
}
